// Aucun jeton sur l'appareil : l'appli s'ouvre seulement avec le lien personnel.
// Sur iPhone, l'appli ajoutée à l'écran d'accueil peut ne pas voir ce que Safari
// a gardé [V] : on peut alors y coller le lien. Champ texte, pas `url` : un jeton
// seul est accepté, et la validation du navigateur bloquerait notre message.
import { type SubmitEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AideContact, Bouton, Erreur, Texte, Titre } from '../components/base';
import { enregistrerJeton, extraireJeton } from '../lib/jeton';

/** Champ « Colle ton lien ici » et bouton « Ouvrir », repris par le lien invalide (/i). */
export function CollerLien({ onLien }: Readonly<{ onLien: () => void }>) {
  const { t } = useTranslation();
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState(false);

  const valider = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const jeton = extraireJeton(saisie);
    if (!jeton) {
      setErreur(true);
      return;
    }
    enregistrerJeton(jeton);
    onLien();
  };

  return (
    <form onSubmit={valider} className="mt-6">
      <label htmlFor="lien" className="block font-medium">
        {t('lien.champ')}
      </label>
      <input
        id="lien"
        type="text"
        inputMode="url"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        value={saisie}
        onChange={(e) => {
          setSaisie(e.target.value);
          setErreur(false);
        }}
        className="border-filet bg-fond mt-2 w-full rounded-lg border px-3 py-3"
        aria-invalid={erreur}
        aria-describedby={erreur ? 'lien-erreur' : undefined}
      />
      {erreur && (
        <p id="lien-erreur" role="alert" className="text-alerte mt-2">
          {t('lien.formatInvalide')}
        </p>
      )}
      <Bouton type="submit" className="mt-4">
        {t('lien.ouvrir')}
      </Bouton>
    </form>
  );
}

export function SansLien({ onLien }: Readonly<{ onLien: () => void }>) {
  const { t } = useTranslation();
  return (
    <section>
      <Titre>{t('lien.titre')}</Titre>
      <Texte>{t('lien.texte')}</Texte>
      <CollerLien onLien={onLien} />
      <AideContact texte={t('lien.pasDeLien')} />
    </section>
  );
}

export function LienRefuse({ onAutre }: Readonly<{ onAutre: () => void }>) {
  const { t } = useTranslation();
  return (
    <section>
      <Titre>{t('lien.refuseTitre')}</Titre>
      <Erreur>{t('lien.refuseTexte')}</Erreur>
      <Bouton variante="secondaire" className="mt-6" onClick={onAutre}>
        {t('lien.autre')}
      </Bouton>
      <AideContact />
    </section>
  );
}
